import { Icon } from '@/common/components/Icon'
import { Button, Modal } from '@/components/ui'
import { cn } from '@/lib/utils'
import { useMemo, useState } from 'react'

interface CameraPermissionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onGranted: () => void
}

type BrowserKind = 'chrome' | 'edge' | 'safari' | 'firefox' | 'other'

const detectBrowser = (): BrowserKind => {
  if (typeof navigator === 'undefined') return 'other'
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('edg/')) return 'edge'
  if (ua.includes('firefox/')) return 'firefox'
  if (ua.includes('chrome/') && !ua.includes('edg/')) return 'chrome'
  if (ua.includes('safari/') && !ua.includes('chrome/')) return 'safari'
  return 'other'
}

const browserInstructions: Record<
  BrowserKind,
  { name: string; steps: string[] }
> = {
  chrome: {
    name: 'Chrome',
    steps: [
      'Click the lock or tune icon on the left side of the address bar.',
      'Find "Camera" in the site settings and change it to "Allow".',
      'Reload the page, then click Try Again.',
    ],
  },
  edge: {
    name: 'Edge',
    steps: [
      'Click the lock icon on the left side of the address bar.',
      'Open "Permissions for this site" and set Camera to "Allow".',
      'Reload the page, then click Try Again.',
    ],
  },
  safari: {
    name: 'Safari',
    steps: [
      'Open the Safari menu and choose "Settings for This Website…".',
      'Set Camera to "Allow".',
      'Reload the page, then click Try Again.',
    ],
  },
  firefox: {
    name: 'Firefox',
    steps: [
      'Click the lock icon on the left side of the address bar.',
      'Next to "Use the Camera (Blocked)", click the "x" to clear the permission.',
      'Reload the page and allow camera access when prompted.',
    ],
  },
  other: {
    name: 'your browser',
    steps: [
      'Open your browser site settings for this page.',
      'Find Camera permissions and set them to "Allow".',
      'Reload the page, then click Try Again.',
    ],
  },
}

export const CameraPermissionModal = ({
  open,
  onOpenChange,
  onGranted,
}: CameraPermissionModalProps) => {
  const [retrying, setRetrying] = useState(false)
  const [stillDenied, setStillDenied] = useState(false)

  const instructions = useMemo(() => browserInstructions[detectBrowser()], [])

  const handleRetry = async () => {
    setRetrying(true)
    setStillDenied(false)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      stream.getTracks().forEach((track) => track.stop())
      setRetrying(false)
      onGranted()
    } catch {
      setRetrying(false)
      setStillDenied(true)
    }
  }

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setStillDenied(false)
      setRetrying(false)
    }
    onOpenChange(nextOpen)
  }

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      size="sm"
      contentClassName="p-0 border-2 border-monochrome-120"
      title={
        <div className="flex items-center gap-4">
          <div
            className={cn(
              'h-[44px] w-[44px] rounded-full',
              'bg-monochrome-5 backdrop-blur-md',
              'border-2 border-monochrome-120',
              'flex items-center justify-center',
              'shadow-[0_12px_40px_rgba(0,0,0,0.45)]',
            )}
          >
            <div
              className={cn(
                'h-[40px] w-[40px] rounded-full',
                'flex items-center justify-center text-accent-1',
              )}
            >
              <Icon icon="camera" width="18" height="18" viewBox="0 0 18 18" />
            </div>
          </div>
          <h2 className="text-title-xxs text-monochrome-1 font-bold">
            Camera Access
          </h2>
        </div>
      }
    >
      <div className="flex flex-col items-center gap-2 border-t border-monochrome-120 pt-6">
        <div
          className={cn(
            'w-[72px] h-[72px] rounded-full',
            'border-2 border-monochrome-120',
            'flex items-center justify-center text-accent-4',
          )}
        >
          <Icon
            icon="warning"
            width="38"
            height="38"
            viewBox="-3 -3 24 24"
            fill="none"
            color="#FFCC00"
          />
        </div>
        <h3 className="text-title-xs font-bold mt-1 text-monochrome-1">
          Camera Access Denied
        </h3>
        <p className="text-monochrome-2 text-body-s text-center">
          We need access to your camera to scan tickets. Follow the steps below
          to enable it in {instructions.name}.
        </p>
      </div>

      <div className="mt-6 border-t border-monochrome-120 pt-6">
        <p className="text-monochrome-1 text-body-s font-medium mb-3">
          How to enable camera access
        </p>
        <ol className="flex flex-col gap-3">
          {instructions.steps.map((step, index) => (
            <li key={index} className="flex items-start gap-3">
              <span
                className={cn(
                  'shrink-0 w-6 h-6 rounded-full',
                  'border border-accent-1 text-monochrome-1',
                  'flex items-center justify-center',
                )}
              >
                {index + 1}
              </span>
              <span className="text-monochrome-2 text-body-s leading-6">
                {step}
              </span>
            </li>
          ))}
        </ol>
        {stillDenied && (
          <p className="text-accent-3 text-body-s text-center mt-4">
            Camera access is still blocked. Please complete the steps above and
            try again.
          </p>
        )}
      </div>

      <div className="flex w-full items-center justify-end gap-2 mt-6 border-t border-monochrome-120 pt-4">
        <Button
          onClick={() => handleClose(false)}
          variant="secondary"
          className="min-w-[154px]"
          disabled={retrying}
        >
          Cancel
        </Button>
        <Button
          onClick={handleRetry}
          variant="primary"
          className="min-w-[154px]"
          disabled={retrying}
        >
          {retrying ? 'Checking...' : 'Try Again'}
        </Button>
      </div>
    </Modal>
  )
}
